"use client";

import { Link } from "@/i18n/navigation";
import Script from "next/script";
import { absoluteLocalizedUrl } from "@/lib/i18n/pageMetadata";

const SITE_URL = "https://vide-maison-bruxelles.be";

const Section = ({ title, children }) => (
  <section className="mt-10">
    <h2 className="text-lg md:text-xl font-semibold tracking-tight text-brand-900">
      {title}
    </h2>
    <div className="mt-3 space-y-3 text-sm md:text-[15px] leading-relaxed text-ink/70">
      {children}
    </div>
  </section>
);

const Bullet = ({ children }) => (
  <li className="ml-5 list-disc text-ink/70 leading-relaxed">{children}</li>
);

const CONTENT = {
  fr: {
    dateLocale: "fr-BE",
    backLink: "← Retour à l'accueil",
    callPrefix: "Appeler",
    emailButton: "Email",
    lastUpdate: "Dernière mise à jour :",
    h1: "Politique de confidentialité — Vide Maison Bruxelles",
    introBefore: "Cette page explique, en langage clair, quelles données ",
    introAfter:
      " collecte lorsque vous demandez un devis de vide maison ou de débarras à Bruxelles, pourquoi, pendant combien de temps, avec qui elles peuvent être partagées, et comment exercer vos droits au titre du RGPD. Elle couvre le formulaire de contact, l'envoi de photos, les cookies du site et les outils de mesure d'audience utilisés lorsque vous y consentez.",
    breadcrumbHome: "Accueil",
    breadcrumbPage: "Politique de confidentialité",
    s1: {
      title: "1) Responsable du traitement",
      addressLabel: "Adresse :",
      emailLabel: "Email :",
      phoneLabel: "Téléphone :",
    },
    s2: {
      title: "2) Données collectées",
      intro: "Nous ne collectons que ce qui est nécessaire pour traiter votre demande de devis et assurer le bon fonctionnement du site :",
      items: [
        "Identité et contact : nom, numéro de téléphone, adresse email si vous la renseignez.",
        "Informations liées à votre demande : commune et adresse d'intervention, type de bien, description du débarras souhaité, urgence indiquée.",
        "Photos éventuellement jointes au formulaire, pour préparer une estimation plus précise.",
        "Données techniques de navigation : adresse IP, localisation approximative (pays/ville) qui en est déduite, type de navigateur, pages visitées et cookies déposés selon votre consentement.",
      ],
    },
    s3: {
      title: "3) Finalités et bases légales (RGPD)",
      items: [
        "Répondre à votre demande de devis, vous recontacter et organiser une éventuelle intervention (exécution de mesures précontractuelles prises à votre demande).",
        "Envoyer votre demande à notre équipe par email et, si vous joignez des photos, les mettre à disposition de l'équipe via un espace de stockage dédié (exécution de mesures précontractuelles).",
        "Mesurer la fréquentation du site et comprendre son usage, uniquement si vous acceptez les cookies de mesure d'audience (consentement).",
        "Sécurité du site, prévention des abus du formulaire et limitation du nombre d'envois (intérêt légitime).",
        "Respect de nos obligations légales, notamment comptables le cas échéant (obligation légale).",
      ],
    },
    s4: {
      title: "4) Cookies et mesure d'audience",
      pBefore: "Notre bannière de cookies distingue deux catégories. Les ",
      strong1: "cookies strictement nécessaires",
      pMid: " (navigation, sécurité, mémorisation de votre choix de consentement) sont toujours actifs et ne nécessitent pas d'accord préalable. Les ",
      strong2: "cookies de mesure d'audience et publicitaires",
      pAfter: " (par exemple Google Analytics et, le cas échéant, le suivi des conversions Google Ads) ne sont déposés qu'après votre consentement explicite, que vous pouvez donner, refuser ou retirer à tout moment via cette bannière ou les paramètres de votre navigateur.",
    },
    s5: {
      title: "5) Durée de conservation",
      items: [
        "Demandes de devis et échanges liés : le temps nécessaire au suivi commercial, puis archivage limité si une obligation légale l'impose.",
        "Photos jointes à une demande : conservées dans l'espace de stockage dédié le temps du traitement du dossier, puis supprimées.",
        "Données de mesure d'audience (si acceptées) : selon la durée de conservation par défaut de l'outil utilisé, généralement de l'ordre de quelques mois à 14 mois.",
      ],
    },
    s6: {
      title: "6) Partage des données",
      intro: "Nous ne vendons ni ne louons vos données à des tiers. Elles peuvent être transmises uniquement aux prestataires techniques indispensables au fonctionnement du site, chacun n'intervenant que pour la finalité qui le concerne :",
      items: [
        "un service d'envoi d'email, pour transmettre votre demande à notre équipe ;",
        "un service de stockage en ligne (Google Drive), uniquement si vous joignez des photos à votre demande ;",
        "notre hébergeur, pour faire fonctionner le site et sa base de données ;",
        "des outils de mesure d'audience et de suivi publicitaire (Google Analytics, Google Ads), uniquement si vous acceptez les cookies correspondants.",
      ],
    },
    s7: {
      title: "7) Vos droits",
      items: [
        "Droit d'accès, de rectification et d'effacement de vos données.",
        "Droit d'opposition et de limitation du traitement.",
        "Droit à la portabilité, dans les cas où il s'applique.",
        "Retrait du consentement aux cookies non essentiels à tout moment.",
      ],
      pBefore: "Pour exercer l'un de ces droits : ",
      pAfter: ". Vous disposez également du droit d'introduire une réclamation auprès de l'Autorité de protection des données belge.",
    },
    s8: {
      title: "8) Sécurité",
      p: "Nous limitons l'accès à vos données à notre équipe et aux prestataires strictement nécessaires, et nous appuyons sur des services d'hébergement et d'envoi d'email reconnus pour leurs pratiques de sécurité. Aucun système n'étant infaillible à 100 %, nous restons attentifs à toute évolution nécessaire de ces mesures.",
    },
    s9: {
      title: "9) Questions fréquentes",
    },
    s10: {
      title: "10) Contact",
      pBefore: "Une question sur cette politique ou sur vos données ? Contactez-nous : ",
      pMid: " ou ",
      pAfter: ". Pour une demande de devis, passez plutôt par notre ",
      pLink: "page de contact",
      pEnd: ".",
    },
    faq: [
      {
        q: "Quelles données collectez-vous quand je demande un devis ?",
        a: "Uniquement ce que vous nous transmettez via le formulaire de contact (nom, téléphone, email optionnel, adresse d'intervention, description du bien et du besoin) et, si vous en joignez, vos photos. Aucune donnée de paiement n'est collectée sur le site : le devis et le règlement se discutent directement avec vous.",
      },
      {
        q: "Que deviennent les photos que je joins à ma demande ?",
        a: "Elles sont envoyées vers un dossier Google Drive dédié à votre demande, utilisé uniquement par notre équipe pour préparer le devis. Elles ne sont ni publiées, ni utilisées à d'autres fins, ni partagées avec des tiers en dehors de cet usage.",
      },
      {
        q: "Utilisez-vous des cookies dès ma première visite ?",
        a: "Seuls les cookies strictement nécessaires au fonctionnement du site (navigation, sécurité, mémorisation de votre choix de consentement) sont actifs par défaut. Les cookies de mesure d'audience et publicitaires ne se déclenchent qu'après votre accord explicite via la bannière de consentement, que vous pouvez modifier à tout moment.",
      },
      {
        q: "Mes données sont-elles partagées avec d'autres entreprises ?",
        a: "Non, nous ne vendons ni ne louons vos données. Elles sont uniquement accessibles à notre équipe et aux prestataires techniques indispensables au fonctionnement du site (hébergement, envoi d'email, stockage des photos, mesure d'audience si vous l'acceptez), chacun agissant selon nos instructions et dans la limite de ce qui est nécessaire.",
      },
      {
        q: "Combien de temps conservez-vous ma demande de devis ?",
        a: "Le temps nécessaire au traitement de votre demande et au suivi commercial, puis selon nos obligations légales de conservation le cas échéant. Vous pouvez demander la suppression de vos données à tout moment tant qu'aucune obligation légale ne s'y oppose.",
      },
      {
        q: "Comment puis-je consulter, corriger ou supprimer mes données ?",
        a: "En nous écrivant à l'adresse email indiquée dans cette page ou en nous appelant directement. Nous répondons à toute demande d'accès, de rectification ou d'effacement dans les meilleurs délais, conformément au RGPD.",
      },
    ],
    quickCta: {
      title: "Besoin d'un devis rapide ?",
      text: "Vide maison, appartement, cave, grenier — intervention rapide à Bruxelles.",
      call: "Appeler maintenant",
      form: "Formulaire",
    },
  },
  nl: {
    dateLocale: "nl-BE",
    backLink: "← Terug naar de startpagina",
    callPrefix: "Bel",
    emailButton: "E-mail",
    lastUpdate: "Laatst bijgewerkt op:",
    h1: "Privacybeleid — Vide Maison Bruxelles",
    introBefore: "Deze pagina legt in duidelijke taal uit welke gegevens ",
    introAfter:
      " verzamelt wanneer u een offerte aanvraagt voor het leegmaken van een huis of een ontruiming in Brussel, waarom, hoe lang, met wie ze eventueel gedeeld worden, en hoe u uw rechten onder de AVG kunt uitoefenen. Ze behandelt het contactformulier, het versturen van foto's, de cookies van de site en de meettools die gebruikt worden wanneer u daarmee instemt.",
    breadcrumbHome: "Home",
    breadcrumbPage: "Privacybeleid",
    s1: {
      title: "1) Verwerkingsverantwoordelijke",
      addressLabel: "Adres:",
      emailLabel: "E-mail:",
      phoneLabel: "Telefoon:",
    },
    s2: {
      title: "2) Verzamelde gegevens",
      intro: "Wij verzamelen enkel wat nodig is om uw offerteaanvraag te behandelen en de goede werking van de site te verzekeren:",
      items: [
        "Identiteit en contact: naam, telefoonnummer, e-mailadres indien u dit invult.",
        "Informatie in verband met uw aanvraag: gemeente en adres van de interventie, type pand, beschrijving van de gewenste ontruiming, aangegeven urgentie.",
        "Eventueel bij het formulier gevoegde foto's, om een nauwkeurigere schatting voor te bereiden.",
        "Technische navigatiegegevens: IP-adres, de daaruit afgeleide, geschatte locatie (land/stad), type browser, bezochte pagina's en geplaatste cookies naargelang uw toestemming.",
      ],
    },
    s3: {
      title: "3) Doeleinden en rechtsgronden (AVG)",
      items: [
        "Uw offerteaanvraag beantwoorden, u opnieuw contacteren en een eventuele interventie organiseren (uitvoering van precontractuele maatregelen op uw verzoek).",
        "Uw aanvraag per e-mail naar ons team versturen en, indien u foto's toevoegt, deze via een specifieke opslagruimte ter beschikking van het team stellen (uitvoering van precontractuele maatregelen).",
        "De bezoekersaantallen van de site meten en het gebruik ervan begrijpen, enkel indien u de cookies voor bezoekersmeting aanvaardt (toestemming).",
        "Beveiliging van de site, preventie van misbruik van het formulier en beperking van het aantal verzendingen (gerechtvaardigd belang).",
        "Naleving van onze wettelijke verplichtingen, met name boekhoudkundige indien van toepassing (wettelijke verplichting).",
      ],
    },
    s4: {
      title: "4) Cookies en bezoekersmeting",
      pBefore: "Onze cookiebanner onderscheidt twee categorieën. De ",
      strong1: "strikt noodzakelijke cookies",
      pMid: " (navigatie, beveiliging, onthouden van uw toestemmingskeuze) zijn steeds actief en vereisen geen voorafgaande toestemming. De ",
      strong2: "cookies voor bezoekersmeting en advertenties",
      pAfter: " (bijvoorbeeld Google Analytics en, in voorkomend geval, de conversietracking van Google Ads) worden pas geplaatst na uw expliciete toestemming, die u op elk moment kunt geven, weigeren of intrekken via deze banner of via de instellingen van uw browser.",
    },
    s5: {
      title: "5) Bewaartermijn",
      items: [
        "Offerteaanvragen en gerelateerde communicatie: de tijd die nodig is voor de commerciële opvolging, daarna beperkte archivering indien een wettelijke verplichting dit oplegt.",
        "Foto's bij een aanvraag: bewaard in de specifieke opslagruimte voor de duur van de behandeling van het dossier, daarna verwijderd.",
        "Gegevens van bezoekersmeting (indien aanvaard): volgens de standaard bewaartermijn van de gebruikte tool, doorgaans van enkele maanden tot 14 maanden.",
      ],
    },
    s6: {
      title: "6) Delen van gegevens",
      intro: "Wij verkopen noch verhuren uw gegevens aan derden. Ze kunnen enkel worden doorgegeven aan de technische dienstverleners die onmisbaar zijn voor de werking van de site, elk uitsluitend voor het doel dat hen aangaat:",
      items: [
        "een e-mailversturingsdienst, om uw aanvraag naar ons team te versturen;",
        "een online opslagdienst (Google Drive), enkel indien u foto's aan uw aanvraag toevoegt;",
        "onze hostingprovider, om de site en zijn database te laten werken;",
        "tools voor bezoekersmeting en advertentietracking (Google Analytics, Google Ads), enkel indien u de bijbehorende cookies aanvaardt.",
      ],
    },
    s7: {
      title: "7) Uw rechten",
      items: [
        "Recht op inzage, rectificatie en verwijdering van uw gegevens.",
        "Recht van verzet en beperking van de verwerking.",
        "Recht op overdraagbaarheid, waar van toepassing.",
        "Intrekking van uw toestemming voor niet-essentiële cookies op elk moment.",
      ],
      pBefore: "Om een van deze rechten uit te oefenen: ",
      pAfter: ". U heeft ook het recht om een klacht in te dienen bij de Belgische Gegevensbeschermingsautoriteit.",
    },
    s8: {
      title: "8) Beveiliging",
      p: "Wij beperken de toegang tot uw gegevens tot ons team en de strikt noodzakelijke dienstverleners, en steunen op hosting- en e-maildiensten die erkend worden voor hun beveiligingspraktijken. Aangezien geen enkel systeem 100% waterdicht is, blijven wij aandachtig voor elke nodige verbetering van deze maatregelen.",
    },
    s9: {
      title: "9) Veelgestelde vragen",
    },
    s10: {
      title: "10) Contact",
      pBefore: "Een vraag over dit beleid of over uw gegevens? Contacteer ons: ",
      pMid: " of ",
      pAfter: ". Voor een offerteaanvraag gaat u beter via onze ",
      pLink: "contactpagina",
      pEnd: ".",
    },
    faq: [
      {
        q: "Welke gegevens verzamelt u wanneer ik een offerte aanvraag?",
        a: "Enkel wat u ons meedeelt via het contactformulier (naam, telefoon, optioneel e-mailadres, interventieadres, beschrijving van het pand en de behoefte) en, indien u ze toevoegt, uw foto's. Er worden geen betaalgegevens op de site verzameld: de offerte en de betaling worden rechtstreeks met u besproken.",
      },
      {
        q: "Wat gebeurt er met de foto's die ik bij mijn aanvraag voeg?",
        a: "Ze worden verstuurd naar een Google Drive-map die specifiek aan uw aanvraag gewijd is en enkel door ons team gebruikt wordt om de offerte voor te bereiden. Ze worden niet gepubliceerd, niet voor andere doeleinden gebruikt en niet met derden gedeeld buiten dit gebruik.",
      },
      {
        q: "Gebruikt u cookies vanaf mijn eerste bezoek?",
        a: "Enkel de cookies die strikt noodzakelijk zijn voor de werking van de site (navigatie, beveiliging, onthouden van uw toestemmingskeuze) zijn standaard actief. De cookies voor bezoekersmeting en advertenties worden pas geactiveerd na uw expliciete toestemming via de toestemmingsbanner, die u op elk moment kunt aanpassen.",
      },
      {
        q: "Worden mijn gegevens gedeeld met andere bedrijven?",
        a: "Nee, wij verkopen noch verhuren uw gegevens. Ze zijn enkel toegankelijk voor ons team en de technische dienstverleners die onmisbaar zijn voor de werking van de site (hosting, e-mailverzending, opslag van foto's, bezoekersmeting indien u dit aanvaardt), elk handelend volgens onze instructies en binnen de grenzen van wat noodzakelijk is.",
      },
      {
        q: "Hoelang bewaart u mijn offerteaanvraag?",
        a: "De tijd die nodig is voor de behandeling van uw aanvraag en de commerciële opvolging, daarna volgens onze wettelijke bewaarverplichtingen indien van toepassing. U kunt op elk moment de verwijdering van uw gegevens aanvragen zolang geen wettelijke verplichting zich daartegen verzet.",
      },
      {
        q: "Hoe kan ik mijn gegevens raadplegen, corrigeren of verwijderen?",
        a: "Door ons te schrijven naar het e-mailadres vermeld op deze pagina of door ons rechtstreeks te bellen. Wij beantwoorden elk verzoek tot inzage, rectificatie of verwijdering zo snel mogelijk, conform de AVG.",
      },
    ],
    quickCta: {
      title: "Snel een offerte nodig?",
      text: "Huis, appartement, kelder, zolder leegmaken — snelle interventie in Brussel.",
      call: "Bel nu",
      form: "Formulier",
    },
  },
  en: {
    dateLocale: "en-GB",
    backLink: "← Back to home",
    callPrefix: "Call",
    emailButton: "Email",
    lastUpdate: "Last updated:",
    h1: "Privacy Policy — Vide Maison Bruxelles",
    introBefore: "This page explains, in plain language, what data ",
    introAfter:
      " collects when you request a house clearance or clear-out quote in Brussels, why, for how long, who it may be shared with, and how to exercise your rights under the GDPR. It covers the contact form, the sending of photos, the site's cookies and the audience measurement tools used when you consent to them.",
    breadcrumbHome: "Home",
    breadcrumbPage: "Privacy policy",
    s1: {
      title: "1) Data controller",
      addressLabel: "Address:",
      emailLabel: "Email:",
      phoneLabel: "Phone:",
    },
    s2: {
      title: "2) Data collected",
      intro: "We only collect what is necessary to process your quote request and to ensure the site runs properly:",
      items: [
        "Identity and contact details: name, phone number, email address if you provide one.",
        "Information related to your request: commune and address of the intervention, type of property, description of the clearance needed, urgency indicated.",
        "Any photos attached to the form, to help prepare a more accurate estimate.",
        "Technical browsing data: IP address, the approximate location (country/city) derived from it, browser type, pages visited, and cookies set according to your consent.",
      ],
    },
    s3: {
      title: "3) Purposes and legal bases (GDPR)",
      items: [
        "Responding to your quote request, contacting you back and organising a potential intervention (performance of pre-contractual measures taken at your request).",
        "Sending your request to our team by email and, if you attach photos, making them available to the team via a dedicated storage space (performance of pre-contractual measures).",
        "Measuring site traffic and understanding its use, only if you accept audience measurement cookies (consent).",
        "Site security, preventing form abuse and limiting the number of submissions (legitimate interest).",
        "Compliance with our legal obligations, notably accounting obligations where applicable (legal obligation).",
      ],
    },
    s4: {
      title: "4) Cookies and audience measurement",
      pBefore: "Our cookie banner distinguishes two categories. ",
      strong1: "Strictly necessary cookies",
      pMid: " (navigation, security, remembering your consent choice) are always active and don't require prior agreement. ",
      strong2: "Audience measurement and advertising cookies",
      pAfter: " (for example Google Analytics and, where applicable, Google Ads conversion tracking) are only set after your explicit consent, which you can give, decline or withdraw at any time via this banner or your browser settings.",
    },
    s5: {
      title: "5) Retention period",
      items: [
        "Quote requests and related exchanges: the time needed for sales follow-up, then limited archiving if a legal obligation requires it.",
        "Photos attached to a request: kept in the dedicated storage space for the duration of processing the file, then deleted.",
        "Audience measurement data (if accepted): according to the default retention period of the tool used, generally from a few months up to 14 months.",
      ],
    },
    s6: {
      title: "6) Data sharing",
      intro: "We do not sell or rent your data to third parties. It may only be passed on to the technical providers essential to the running of the site, each acting only for the purpose that concerns them:",
      items: [
        "an email delivery service, to send your request to our team;",
        "an online storage service (Google Drive), only if you attach photos to your request;",
        "our hosting provider, to run the site and its database;",
        "audience measurement and advertising tracking tools (Google Analytics, Google Ads), only if you accept the corresponding cookies.",
      ],
    },
    s7: {
      title: "7) Your rights",
      items: [
        "Right of access, rectification and erasure of your data.",
        "Right to object and to restrict processing.",
        "Right to data portability, where applicable.",
        "Right to withdraw consent to non-essential cookies at any time.",
      ],
      pBefore: "To exercise any of these rights: ",
      pAfter: ". You also have the right to lodge a complaint with the Belgian Data Protection Authority.",
    },
    s8: {
      title: "8) Security",
      p: "We limit access to your data to our team and to strictly necessary providers, and we rely on hosting and email services recognised for their security practices. As no system is 100% infallible, we remain attentive to any necessary improvements to these measures.",
    },
    s9: {
      title: "9) Frequently asked questions",
    },
    s10: {
      title: "10) Contact",
      pBefore: "A question about this policy or about your data? Contact us: ",
      pMid: " or ",
      pAfter: ". For a quote request, please use our ",
      pLink: "contact page",
      pEnd: " instead.",
    },
    faq: [
      {
        q: "What data do you collect when I request a quote?",
        a: "Only what you provide us via the contact form (name, phone, optional email, intervention address, description of the property and the need) and, if you attach any, your photos. No payment data is collected on the site: the quote and payment are discussed directly with you.",
      },
      {
        q: "What happens to the photos I attach to my request?",
        a: "They're sent to a Google Drive folder dedicated to your request, used only by our team to prepare the quote. They are not published, not used for any other purpose, and not shared with third parties beyond this use.",
      },
      {
        q: "Do you use cookies from my first visit?",
        a: "Only the cookies strictly necessary for the site to function (navigation, security, remembering your consent choice) are active by default. Audience measurement and advertising cookies are only triggered after your explicit agreement via the consent banner, which you can change at any time.",
      },
      {
        q: "Is my data shared with other companies?",
        a: "No, we neither sell nor rent your data. It's only accessible to our team and to the technical providers essential to the site's operation (hosting, email delivery, photo storage, audience measurement if you accept it), each acting according to our instructions and within what's necessary.",
      },
      {
        q: "How long do you keep my quote request?",
        a: "For as long as needed to process your request and for sales follow-up, then according to our legal retention obligations where applicable. You can request deletion of your data at any time, as long as no legal obligation prevents it.",
      },
      {
        q: "How can I access, correct or delete my data?",
        a: "By writing to the email address shown on this page or by calling us directly. We respond to any request for access, rectification or erasure as promptly as possible, in accordance with the GDPR.",
      },
    ],
    quickCta: {
      title: "Need a quick quote?",
      text: "House, apartment, cellar, attic clearance — fast intervention in Brussels.",
      call: "Call now",
      form: "Form",
    },
  },
};

export default function PageClient({ locale }) {
  const c = CONTENT[locale];

  const brand = {
    name: "Vide Maison Bruxelles",
    email: "contact@vide-maison-bruxelles.be",
    phoneDisplay: "+32 (0)493 96 65 07",
    phoneHref: "+32493966507",
    address: "Rue de la Rive 72, 1200 Woluwe-Saint-Lambert, Bruxelles, Belgique",
  };

  const breadcrumbJsonLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: c.breadcrumbHome, item: absoluteLocalizedUrl(locale, "") },
      {
        "@type": "ListItem",
        position: 2,
        name: c.breadcrumbPage,
        item: absoluteLocalizedUrl(locale, "/politique-de-confidentialite"),
      },
    ],
  };

  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: c.faq.map((item) => ({
      "@type": "Question",
      name: item.q,
      acceptedAnswer: { "@type": "Answer", text: item.a },
    })),
  };

  return (
    <div className="bg-sand">
      <meta name="color-scheme" content="light only" />

      <Script
        id="breadcrumb-jsonld"
        type="application/ld+json"
        strategy="afterInteractive"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbJsonLd) }}
      />
      <Script
        id="faq-jsonld"
        type="application/ld+json"
        strategy="afterInteractive"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqJsonLd) }}
      />

      <div
        aria-hidden="true"
        className="pointer-events-none fixed inset-0"
        style={{
          background:
            "radial-gradient(900px 420px at 10% 0%, rgba(63,108,81,0.06), transparent 60%), radial-gradient(900px 420px at 85% 15%, rgba(27,75,51,0.05), transparent 55%)",
        }}
      />

      <div className="relative mx-auto max-w-[1100px] px-5 md:px-8 py-14 md:py-16">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <Link
            href="/"
            className="inline-flex items-center gap-2 rounded-2xl border border-sand-line bg-white px-4 py-2 text-sm text-ink/70 hover:border-accent hover:text-accent transition"
          >
            {c.backLink}
          </Link>

          <div className="flex items-center gap-2">
            <a
              href={`tel:${brand.phoneHref}`}
              className="inline-flex items-center justify-center rounded-2xl bg-ink px-4 py-2 text-sm font-semibold text-white hover:bg-ink-light transition"
            >
              {c.callPrefix} {brand.phoneDisplay}
            </a>
            <a
              href={`mailto:${brand.email}`}
              className="inline-flex items-center justify-center rounded-2xl border border-ink/15 bg-white px-4 py-2 text-sm font-semibold text-ink hover:border-accent hover:text-accent transition"
            >
              {c.emailButton}
            </a>
          </div>
        </div>

        <div className="mt-8 overflow-hidden rounded-[32px] border border-sand-line bg-white shadow-[0_20px_60px_rgba(20,32,26,0.08)]">
          <div className="relative px-6 md:px-10 py-10 md:py-12">
            <p className="text-xs md:text-sm text-ink/50">
              {c.lastUpdate}{" "}
              {new Date().toLocaleDateString(c.dateLocale, { timeZone: "Europe/Brussels" })}
            </p>

            <h1 className="mt-3 text-2xl md:text-4xl font-bold tracking-tight text-ink">
              {c.h1}
            </h1>
            <p className="mt-3 text-sm md:text-[15px] leading-relaxed text-ink/70 max-w-[80ch]">
              {c.introBefore}
              <span className="text-ink font-semibold">{brand.name}</span>
              {c.introAfter}
            </p>

            <Section title={c.s1.title}>
              <p>
                <span className="text-ink font-semibold">{brand.name}</span>
                <br />
                {c.s1.addressLabel} {brand.address}
                <br />
                {c.s1.emailLabel}{" "}
                <a className="underline underline-offset-4 text-accent hover:text-accent-dark" href={`mailto:${brand.email}`}>
                  {brand.email}
                </a>
                <br />
                {c.s1.phoneLabel}{" "}
                <a className="underline underline-offset-4 text-accent hover:text-accent-dark" href={`tel:${brand.phoneHref}`}>
                  {brand.phoneDisplay}
                </a>
              </p>
            </Section>

            <Section title={c.s2.title}>
              <p>{c.s2.intro}</p>
              <ul className="space-y-2">
                {c.s2.items.map((item) => (
                  <Bullet key={item}>{item}</Bullet>
                ))}
              </ul>
            </Section>

            <Section title={c.s3.title}>
              <ul className="space-y-2">
                {c.s3.items.map((item) => (
                  <Bullet key={item}>{item}</Bullet>
                ))}
              </ul>
            </Section>

            <Section title={c.s4.title}>
              <p>
                {c.s4.pBefore}
                <span className="text-ink font-semibold">{c.s4.strong1}</span>
                {c.s4.pMid}
                <span className="text-ink font-semibold">{c.s4.strong2}</span>
                {c.s4.pAfter}
              </p>
            </Section>

            <Section title={c.s5.title}>
              <ul className="space-y-2">
                {c.s5.items.map((item) => (
                  <Bullet key={item}>{item}</Bullet>
                ))}
              </ul>
            </Section>

            <Section title={c.s6.title}>
              <p>{c.s6.intro}</p>
              <ul className="space-y-2">
                {c.s6.items.map((item) => (
                  <Bullet key={item}>{item}</Bullet>
                ))}
              </ul>
            </Section>

            <Section title={c.s7.title}>
              <ul className="space-y-2">
                {c.s7.items.map((item) => (
                  <Bullet key={item}>{item}</Bullet>
                ))}
              </ul>
              <p>
                {c.s7.pBefore}
                <a className="underline underline-offset-4 text-accent hover:text-accent-dark" href={`mailto:${brand.email}`}>
                  {brand.email}
                </a>
                {c.s7.pAfter}
              </p>
            </Section>

            <Section title={c.s8.title}>
              <p>{c.s8.p}</p>
            </Section>

            <Section title={c.s9.title}>
              <div className="space-y-4">
                {c.faq.map((item) => (
                  <div key={item.q} className="rounded-2xl border border-sand-line bg-sand p-4">
                    <p className="font-semibold text-ink">{item.q}</p>
                    <p className="mt-1.5 text-sm leading-relaxed text-ink/70">{item.a}</p>
                  </div>
                ))}
              </div>
            </Section>

            <Section title={c.s10.title}>
              <p>
                {c.s10.pBefore}
                <a className="underline underline-offset-4 text-accent hover:text-accent-dark" href={`mailto:${brand.email}`}>
                  {brand.email}
                </a>
                {c.s10.pMid}
                <a className="underline underline-offset-4 text-accent hover:text-accent-dark" href={`tel:${brand.phoneHref}`}>
                  {brand.phoneDisplay}
                </a>
                {c.s10.pAfter}
                <Link href="/contact" className="underline underline-offset-4 text-accent hover:text-accent-dark">
                  {c.s10.pLink}
                </Link>
                {c.s10.pEnd}
              </p>
            </Section>

            <div className="mt-10 flex flex-col md:flex-row md:items-center gap-3 justify-between rounded-[24px] border border-sand-line bg-sand p-5">
              <div>
                <p className="text-ink font-semibold">{c.quickCta.title}</p>
                <p className="text-sm text-ink/65">{c.quickCta.text}</p>
              </div>
              <div className="flex gap-2">
                <a
                  href={`tel:${brand.phoneHref}`}
                  className="inline-flex items-center justify-center rounded-2xl bg-ink px-5 py-3 text-sm font-semibold text-white hover:bg-ink-light transition"
                >
                  {c.quickCta.call}
                </a>
                <Link
                  href="/contact"
                  className="inline-flex items-center justify-center rounded-2xl border-2 border-ink/15 bg-white px-5 py-3 text-sm font-semibold text-ink hover:border-accent hover:text-accent transition"
                >
                  {c.quickCta.form}
                </Link>
              </div>
            </div>

            <p className="mt-6 text-xs text-ink/45">
              © {new Date().getFullYear()} {brand.name} — {SITE_URL.replace("https://", "")}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
